'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { useEffect, useRef, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { fetchStoreReviews } from '@eliadmin/core/src/api/general/general-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { FaStar } from 'react-icons/fa';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import { HtmlDescription } from '../shared/html-description';

export interface Review {
  id: number;
  name: string;
  review: string;
  review_type: {
    id: number;
    name: string;
    type: string;
  };
  rating: string;
}

export const ReviewsCarousel = () => {
  const [width, setWidth] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();

  useEffect(() => {
    if (carouselRef.current) {
      const totalWidth = carouselRef.current.scrollWidth;
      setWidth(totalWidth / 2);

      // Start animation
      controls.start({
        x: -totalWidth / 2,
        transition: {
          x: {
            repeat: Infinity,
            repeatType: 'loop',
            duration: 120,
            ease: 'linear',
          },
        },
      });
    }
  }, [controls]);

  const locale = useLocaleStore(s => s.currentLocale);

  const { data: reviews = { data: [] } } = useQuery({
    queryKey: ['REVIEWS', locale],
    queryFn: () => fetchStoreReviews<Review>({ locale }),
  });

  return (
    <div className="mt-16 w-full overflow-hidden sm:mt-20 lg:mt-24">
      <motion.div ref={carouselRef} className="flex w-full" animate={controls}>
        {[...reviews.data, ...reviews.data].map((review, index) => (
          <div key={`${review.id}-${index}`} className="flex-shrink-0 px-2">
            <ReviewCard review={review} />
          </div>
        ))}
      </motion.div>
    </div>
  );
};

function ReviewCard({ review }: { review: Review }) {
  return (
    <motion.div
      className={cn(
        'relative flex w-[260px] flex-col items-start justify-start gap-12 border border-border bg-transparent p-6 sm:w-[320px] md:w-[416px] md:p-8',
      )}
      transition={{ type: 'spring', stiffness: 300 }}
    >
      <div className="flex items-center justify-start gap-2">
        <div className="flex items-center justify-start gap-1">
          {[...Array(5)].map((_, i) => (
            <FaStar key={i} size={20} className="text-[#FEBB01]" />
          ))}
        </div>
        <p className="text-sm">
          <b className="font-bold">5</b>/5
        </p>
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-6">
        <HtmlDescription
          content={review.review}
          color="foreground"
          className="ea-reviews__description line-clamp-4 h-20 w-full text-sm text-foreground"
          size="sm"
        />
        <div className="flex w-full flex-col items-start justify-start gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col items-start justify-start gap-1">
            <p className="text-base font-semibold text-foreground">{review.name}</p>
          </div>
        </div>
      </div>
      <Image
        src={'/images/google.svg'}
        alt="Google recenzia"
        width={160}
        height={1}
        preserveAspectRatio="width"
        unoptimized
        className="absolute bottom-3.5 right-5 h-14 w-auto object-contain object-center"
      />
    </motion.div>
  );
}
